/**
 * Admin tab (Loan Drilldown) — admins and super admins only.
 *
 * A reference of every permission-gated area on the drilldown: what it is,
 * who can use it, the rule in words, whether the backend also enforces it, a
 * live "You" check for the signed-in user on THIS loan, and the actual list of
 * users who pass the rule. The data is the hand-maintained catalog in
 * `drilldownPermissionsCatalog.js` — update it whenever a gate changes.
 *
 * One table per area type (Tab first — see KIND_ORDER). The Users column
 * comes from ONE fetch (GET
 * /users/all_users); each user record is run through the same
 * `resolvePermissions` the app uses for the signed-in user, so the list can't
 * drift from the real gate. The one signal that can't be evaluated from a user
 * record is underwriting org-group membership (a per-user lookup) — rows that
 * depend on it say so.
 *
 * Read-only. Nav entry + route are gated on `isAdmin` in LoanDrilldownScreen
 * (isAdmin already includes super admins).
 */
import { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { resolvePermissions } from "../../../../hooks/usePermissionsHook";
import { isHighLevelEmail } from "../../../../hooks/useReduxHook";
import { useEnv } from "../../../../hooks/useEnv";
import { useCanEditUnderwritingNotes } from "../../../../hooks/useCanEditUnderwritingNotes";
import { useUsersApi } from "../../../../hooks/useAdminUsersHook";
import { emailInRoles } from "../../../../constants/selectOptions/selectOptionsLoanRoles";
import { usePipeline, useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { slate, status } from "../../../../theme/colors";
import { canSeeLoanChat } from "../LoanDrilldownChatTab/loanChatAccess";
import { canSeeOfficeWebView } from "../LoanDrilldownDocumentsTab/officeWebViewAccess";
import { LoanSectionCard, LoanTabShell, TAB_TOKENS as T } from "../../shared/tabLayout";
import {
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_TABLE_BOX_SX,
} from "../../shared/cardGrid";
import {
  ENFORCED,
  GATED_AREAS,
  KIND_ORDER,
  ROLE_SIGNALS,
  WHO_MATCHERS,
} from "./drilldownPermissionsCatalog";


const sameEmail = (a, b) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

// Rows whose rule leans on underwriting org-group membership, which only the
// signed-in user's context resolves (it's a per-user lookup).
const GROUP_DEPENDENT = new Set(["uw-note-edit", "uwGroup"]);

/**
 * Everything a catalog rule reads, for ONE user record. `shared` carries the
 * loan / pipeline / build facts that are the same for everyone; `overrides`
 * lets the signed-in user pass signals only a hook can resolve.
 */
const buildUserContext = (user, shared, overrides = {}) => {
  const email = user?.emailAddress;
  const perms = resolvePermissions(user);
  return {
    ...shared,
    ...perms,
    isHighLevelUser: isHighLevelEmail(email),
    // Org-group membership isn't on the user record; the roster + flags are.
    canEditUwNotes:
      perms.isAdmin ||
      perms.isUnderwriter ||
      emailInRoles(email, ["underwriter", "preUnderwriter"]),
    canSeeChat: canSeeLoanChat(email),
    canSeeOfficeView: canSeeOfficeWebView(email),
    isAssignedCloser: sameEmail(shared.loan?.loanCloser, email),
    isCloserRoster: emailInRoles(email, ["closers"]),
    isAdminRoster: emailInRoles(email, ["admin"]),
    isUwOrAdminRoster: emailInRoles(email, ["underwriter", "admin"]),
    ...overrides,
  };
};

/**
 * Split the users who pass a row into one section per "Who" label. A user sits
 * under every label they match; anyone who passes through a rule-specific
 * clause and matches no label goes under "Other".
 */
const groupByWho = (passing, who = []) => {
  const groups = who
    .filter((label) => WHO_MATCHERS[label])
    .map((label) => ({
      label,
      users: passing.filter(({ ctx }) => WHO_MATCHERS[label](ctx)).map(({ user }) => user),
    }));
  const other = passing
    .filter(({ ctx }) => !who.some((label) => WHO_MATCHERS[label]?.(ctx)))
    .map(({ user }) => user);
  if (other.length) groups.push({ label: "Other (rule-specific)", users: other });
  return groups.filter((g) => g.users.length > 0);
};

const userName = (u) =>
  u?.fullName ||
  [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
  u?.emailAddress ||
  "Unknown user";

/* ── cells ─────────────────────────────────────────────── */

const Pill = ({ tone, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "8px",
      height: 20,
      borderRadius: "999px",
      background: tone.bg,
      color: tone.fg,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
      lineHeight: 1,
    }}
  >
    {children}
  </Box>
);

const ENFORCED_META = {
  [ENFORCED.SERVER]: { label: "Server + screen", tone: status.success },
  [ENFORCED.SCREEN]: { label: "Screen only", tone: status.warning },
  [ENFORCED.NONE]: { label: "Not gated", tone: status.neutral },
};

// "You" is two halves: the permission, then whether the area exists on this loan.
const youState = (row, ctx) => {
  const allowed = !!row.allowed(ctx);
  const here = row.shownHere ? !!row.shownHere(ctx) : true;
  if (allowed && here) return { key: 2, label: "Allowed", tone: status.success };
  if (allowed) {
    return {
      key: 1,
      label: "Not on loan",
      tone: status.info,
      hint: "You pass the permission, but this area doesn't appear on this loan (product, pipeline or status).",
    };
  }
  return { key: 0, label: "No access", tone: status.neutral };
};

const WrapText = ({ children, strong, muted, size = 12.5 }) => (
  <Box
    sx={{
      py: "9px",
      whiteSpace: "normal",
      lineHeight: 1.45,
      fontSize: size,
      fontWeight: strong ? 600 : 400,
      color: muted ? slate[500] : slate[800],
    }}
  >
    {children}
  </Box>
);

const AreaCell = ({ data }) => (
  <Flx column sx={{ py: "9px", lineHeight: 1.35, whiteSpace: "normal" }}>
    <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: slate[900] }}>{data.tab}</Txt>
    <Txt sx={{ fontSize: 12, color: slate[500] }}>{data.location}</Txt>
  </Flx>
);

const WhoCell = ({ value }) => (
  <Flx ac wrap g={0.5} sx={{ height: "100%", py: "8px" }}>
    {(value || []).map((w) => (
      <Pill key={w} tone={status.neutral}>
        {w}
      </Pill>
    ))}
  </Flx>
);

const EnforcedCell = ({ data }) => {
  const meta = ENFORCED_META[data.enforced] || ENFORCED_META[ENFORCED.SCREEN];
  const pill = <Pill tone={meta.tone}>{meta.label}</Pill>;
  return (
    <Flx ac sx={{ height: "100%" }}>
      {data.enforcedNote ? (
        <Tooltip arrow title={data.enforcedNote}>
          <span>{pill}</span>
        </Tooltip>
      ) : (
        pill
      )}
    </Flx>
  );
};

const YouCell = ({ value }) =>
  value ? (
    <Flx ac sx={{ height: "100%" }}>
      {value.hint ? (
        <Tooltip arrow title={value.hint}>
          <span>
            <Pill tone={value.tone}>{value.label}</Pill>
          </span>
        </Tooltip>
      ) : (
        <Pill tone={value.tone}>{value.label}</Pill>
      )}
    </Flx>
  ) : null;

const HasCell = ({ value }) => (
  <Flx ac sx={{ height: "100%" }}>
    <Pill tone={value ? status.success : status.neutral}>{value ? "Yes" : "No"}</Pill>
  </Flx>
);

const matchesUser = (u, needle) =>
  !needle || `${userName(u)} ${u?.emailAddress || ""}`.toLowerCase().includes(needle);

const UserRow = ({ user }) => (
  <Flx
    column
    sx={{ pl: 4, pr: 1.5, py: 0.6, "&:hover": { background: T.color.surfaceSunk } }}
  >
    <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: T.color.ink }}>{userName(user)}</Txt>
    <Txt sx={{ fontSize: 11.5, color: T.color.ink3 }}>{user?.emailAddress}</Txt>
  </Flx>
);

/**
 * One collapsible "Who" section of the users popover. Collapsed by default when
 * the popover has several sections; forced open while a filter is typed so the
 * matches are visible.
 */
const WhoSection = ({ label, users, defaultOpen, forceOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = forceOpen || open;
  return (
    <Box sx={{ borderBottom: `1px solid ${T.color.border}`, "&:last-of-type": { borderBottom: 0 } }}>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        sx={{ width: "100%", justifyContent: "flex-start", gap: 0.75, px: 1.5, py: 1 }}
      >
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 18,
            color: T.color.muted,
            transition: "transform 0.15s",
            transform: expanded ? "none" : "rotate(-90deg)",
          }}
        />
        <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.color.ink, flex: 1, textAlign: "left" }}>
          {label}
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: T.color.muted }}>{users.length}</Txt>
      </ButtonBase>
      {expanded && (
        <Box sx={{ pb: 0.5 }}>
          {users.map((u) => (
            <UserRow key={u?._id || u?.emailAddress} user={u} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * The count of users who pass the rule; click for the list, split into one
 * collapsible section per "Who" group. `users` is null while the one all-users
 * fetch is in flight, and `error` when it failed.
 */
const UsersCell = ({ data, context }) => {
  const [anchor, setAnchor] = useState(null);
  const [q, setQ] = useState("");
  const { loading, error, total } = context || {};
  const list = data?.users;
  const needle = q.trim().toLowerCase();

  const groups = useMemo(
    () =>
      (data?.userGroups || [])
        .map((g) => ({ ...g, users: g.users.filter((u) => matchesUser(u, needle)) }))
        .filter((g) => g.users.length > 0),
    [data?.userGroups, needle],
  );

  if (loading) {
    return (
      <Flx ac sx={{ height: "100%", fontSize: 12, color: slate[400] }}>Loading…</Flx>
    );
  }
  if (error || !list) {
    return (
      <Flx ac sx={{ height: "100%", fontSize: 12, color: slate[400] }}>—</Flx>
    );
  }

  const everyone = total > 0 && list.length === total;
  const label = list.length === 0 ? "None" : everyone ? `All ${total}` : `${list.length} users`;
  const single = (data?.userGroups || []).length <= 1;

  return (
    <Flx ac sx={{ height: "100%" }}>
      <ButtonBase
        disabled={list.length === 0}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ borderRadius: "999px" }}
      >
        <Pill tone={list.length === 0 ? status.neutral : status.info}>{label}</Pill>
      </ButtonBase>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQ("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: 360, maxHeight: 480, display: "flex", flexDirection: "column" } },
        }}
      >
        <Flx column g={1} sx={{ p: 1.5, borderBottom: `1px solid ${T.color.border}` }}>
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: T.color.ink }}>
            {data.tab} — {data.location}
          </Txt>
          <Txt sx={{ fontSize: 12, color: T.color.ink3 }}>
            {list.length} of {total} users pass this rule
            {data.shownHere ? " (on loans where the area appears)" : ""}. Someone in
            more than one group is listed under each.
          </Txt>
          {GROUP_DEPENDENT.has(data.id) && (
            <Txt sx={{ fontSize: 12, color: status.warning.fg }}>
              Underwriting org-group members aren't included — that membership is
              looked up per user.
            </Txt>
          )}
          <TextField
            size="small"
            value={q}
            placeholder="Filter users"
            onChange={(e) => setQ(e.target.value)}
            inputProps={{ "aria-label": "Filter users" }}
            InputProps={{ sx: { fontSize: 12.5, height: 30 } }}
          />
        </Flx>
        <Box sx={{ overflowY: "auto" }}>
          {groups.map((g) => (
            <WhoSection
              key={g.label}
              label={g.label}
              users={g.users}
              defaultOpen={single}
              forceOpen={!!needle}
            />
          ))}
          {groups.length === 0 && (
            <Txt component="div" sx={{ px: 1.5, py: 1, fontSize: 12, color: T.color.muted }}>
              No matches.
            </Txt>
          )}
        </Box>
      </Popover>
    </Flx>
  );
};

/* ── grids ─────────────────────────────────────────────── */


const AREA_COLUMNS = [
  {
    // Sorts by tab; the cell shows tab + location.
    headerName: "Area",
    colId: "area",
    valueGetter: (p) => p.data?.tab,
    cellRenderer: AreaCell,
    flex: 1.4,
    minWidth: 220,
    autoHeight: true,
    wrapText: true,
  },
  {
    headerName: "Who",
    field: "who",
    valueGetter: (p) => (p.data?.who || []).join(", "),
    cellRenderer: (p) => <WhoCell value={p.data.who} />,
    flex: 1,
    minWidth: 170,
    autoHeight: true,
  },
  {
    headerName: "Rule",
    field: "rule",
    cellRenderer: (p) => <WrapText>{p.value}</WrapText>,
    flex: 2.4,
    minWidth: 280,
    autoHeight: true,
    wrapText: true,
  },
  {
    headerName: "Enforced",
    field: "enforced",
    valueGetter: (p) => ENFORCED_META[p.data?.enforced]?.label,
    cellRenderer: EnforcedCell,
    width: 140,
  },
  {
    headerName: "Users",
    colId: "users",
    valueGetter: (p) => p.data?.users?.length ?? null,
    cellRenderer: UsersCell,
    width: 120,
  },
  {
    headerName: "You",
    colId: "you",
    valueGetter: (p) => p.data?.you?.label,
    comparator: (a, b, na, nb) =>
      (na?.data?.you?.key ?? 0) - (nb?.data?.you?.key ?? 0),
    cellRenderer: (p) => <YouCell value={p.data.you} />,
    width: 115,
  },
];

const ROLE_COLUMNS = [
  {
    headerName: "Role",
    field: "label",
    cellRenderer: (p) => <WrapText strong>{p.value}</WrapText>,
    width: 190,
    autoHeight: true,
  },
  {
    headerName: "How it's decided",
    field: "resolvedBy",
    cellRenderer: (p) => <WrapText>{p.value}</WrapText>,
    flex: 2,
    minWidth: 260,
    autoHeight: true,
    wrapText: true,
  },
  {
    headerName: "Backend",
    field: "backend",
    cellRenderer: (p) => <WrapText muted>{p.value}</WrapText>,
    flex: 1.4,
    minWidth: 200,
    autoHeight: true,
    wrapText: true,
  },
  {
    headerName: "Users",
    colId: "users",
    valueGetter: (p) => p.data?.users?.length ?? null,
    cellRenderer: (p) => (
      <UsersCell
        data={{ ...p.data, tab: p.data.label, location: "role" }}
        context={p.context}
      />
    ),
    width: 120,
  },
  {
    headerName: "You",
    field: "has",
    valueGetter: (p) => (p.data.has ? "Yes" : "No"),
    cellRenderer: (p) => <HasCell value={p.data.has} />,
    width: 90,
  },
];


const Grid = ({ rows, columns, context }) => (
  <Box sx={CARD_TABLE_BOX_SX}>
    <AgGridTableWrapper compactTable noBorder suppressMinHeightWhenPopulated={rows} sx={CARD_GRID_SX}>
      <AgGridReact
        rowData={rows}
        columnDefs={columns}
        defaultColDef={CARD_GRID_COL_DEF}
        getRowId={(p) => p.data.id}
        domLayout="autoHeight"
        context={context}
        suppressCellFocus
      />
    </AgGridTableWrapper>
  </Box>
);

const KIND_SUBTITLES = {
  Tab: "Whole tabs in the drilldown nav that only some people see.",
  Action: "Buttons and menu items inside a tab.",
  Panel: "Cards and side panels that render only for some people.",
  "Edit access": "Places everyone can read but only some people can change.",
  View: "Alternate views and read-only surfaces.",
};

const rowMatches = (row, needle) =>
  !needle ||
  [row.tab, row.location, row.kind, row.rule, ...(row.who || [])]
    .join(" ")
    .toLowerCase()
    .includes(needle);

/* ── tab ───────────────────────────────────────────────── */

const LoanDrilldownAdminTab = () => {
  const me = useSelector((s) => s.user);
  const loan = useSelector((s) => s.loanDrilldown);
  const { isResfin, isLendmarq, isResilender } = useEnv();
  const canEditUwNotes = useCanEditUnderwritingNotes();
  const { capabilities } = usePipeline();
  const isAcquisitions = useIsAcquisitions();
  const { listAllUsers } = useUsersApi();
  const [query, setQuery] = useState("");

  // The one fetch: every user, evaluated locally against each rule.
  const [allUsers, setAllUsers] = useState(null);
  const [usersError, setUsersError] = useState(null);
  useEffect(() => {
    let active = true;
    listAllUsers()
      .then((data) => active && setAllUsers(Array.isArray(data) ? data : []))
      .catch((e) => active && setUsersError(e?.message || "Failed to load users"));
    return () => {
      active = false;
    };
  }, [listAllUsers]);

  const shared = useMemo(
    () => ({
      isResfin: !!isResfin,
      isLendmarq: !!isLendmarq,
      isResilender: !!isResilender,
      capabilities,
      isAcquisitions,
      loan,
    }),
    [isResfin, isLendmarq, isResilender, capabilities, isAcquisitions, loan],
  );

  // The signed-in user also gets the org-group half of the UW-note rule.
  const myCtx = useMemo(
    () => buildUserContext(me, shared, { canEditUwNotes: !!canEditUwNotes }),
    [me, shared, canEditUwNotes],
  );

  const userCtxs = useMemo(
    () =>
      allUsers
        ?.filter((u) => u?.emailAddress)
        .map((u) => ({ user: u, ctx: buildUserContext(u, shared) }))
        .sort((a, b) => userName(a.user).localeCompare(userName(b.user))) ?? null,
    [allUsers, shared],
  );

  const areaRows = useMemo(
    () =>
      GATED_AREAS.map((row) => {
        const passing = userCtxs ? userCtxs.filter(({ ctx }) => row.allowed(ctx)) : null;
        return {
          ...row,
          you: youState(row, myCtx),
          users: passing ? passing.map(({ user }) => user) : null,
          userGroups: passing ? groupByWho(passing, row.who) : null,
        };
      }),
    [myCtx, userCtxs],
  );

  const roleRows = useMemo(
    () =>
      ROLE_SIGNALS.map((r) => {
        const users = userCtxs
          ? userCtxs.filter(({ ctx }) => r.has(ctx)).map(({ user }) => user)
          : null;
        return {
          ...r,
          id: r.key,
          has: !!r.has(myCtx),
          users,
          userGroups: users ? [{ label: r.label, users }] : null,
        };
      }),
    [myCtx, userCtxs],
  );

  const needle = query.trim().toLowerCase();
  const sections = useMemo(
    () =>
      KIND_ORDER.map((kind) => ({
        kind,
        total: areaRows.filter((r) => r.kind === kind).length,
        rows: areaRows.filter((r) => r.kind === kind && rowMatches(r, needle)),
      })).filter((s) => s.rows.length > 0),
    [areaRows, needle],
  );

  const gridContext = useMemo(
    () => ({
      loading: !allUsers && !usersError,
      error: usersError,
      total: userCtxs?.length ?? 0,
    }),
    [allUsers, usersError, userCtxs],
  );

  const kpis = useMemo(() => {
    const count = (fn) => areaRows.filter(fn).length;
    return [
      { key: "areas", label: "Gated areas", value: String(areaRows.length) },
      {
        key: "you",
        label: "You can use here",
        value: String(count((r) => r.you.key === 2)),
        tone: "ok",
      },
      {
        key: "server",
        label: "Enforced by server",
        value: String(count((r) => r.enforced === ENFORCED.SERVER)),
      },
      {
        key: "screen",
        label: "Screen only",
        value: String(count((r) => r.enforced === ENFORCED.SCREEN)),
        tone: "warn",
      },
    ];
  }, [areaRows]);

  return (
    <LoanTabShell
      title="Admin"
      subTitle="Every permission-gated area on this loan file, who can use it, and the rule behind it."
      kpis={kpis}
      endContent={
        <TextField
          size="small"
          value={query}
          placeholder="Search area, role or rule"
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": "Search gated areas" }}
          InputProps={{
            sx: { fontSize: 12.5, height: 32, background: "#fff" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 16, color: T.color.muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
      }
    >
      {usersError && (
        <Txt component="div" sx={{ fontSize: 12.5, color: status.warning.fg }}>
          Couldn't load the user list ({usersError}) — the Users column is empty; the
          rules and the You column still apply.
        </Txt>
      )}

      {sections.map((s) => (
        <LoanSectionCard
          key={s.kind}
          title={s.kind === "Edit access" ? "Edit access" : `${s.kind}s`}
          subTitle={`${KIND_SUBTITLES[s.kind] || ""} Click a Users count for the names.`}
          count={needle ? `${s.rows.length} of ${s.total}` : `${s.total} total`}
        >
          <Grid rows={s.rows} columns={AREA_COLUMNS} context={gridContext} />
        </LoanSectionCard>
      ))}
      {sections.length === 0 && (
        <Txt component="div" sx={{ fontSize: 12.5, color: T.color.ink3 }}>
          No gated areas match “{query}”.
        </Txt>
      )}

      <LoanSectionCard
        title="Roles"
        subTitle="The signals the rules above are built from, how each one is decided, and who has it."
        count={`${roleRows.length} total`}
      >
        <Grid rows={roleRows} columns={ROLE_COLUMNS} context={gridContext} />
      </LoanSectionCard>
    </LoanTabShell>
  );
};

export default memo(LoanDrilldownAdminTab);
